import { useState, useEffect } from 'react';
import { 
  Settings,
  Info,
  Bell,
  Shield,
  Globe,
  Moon,
  Sun,
  Mail,
  Phone,
  Clock,
  AlertTriangle,
  Activity,
  Thermometer,
  Users,
  Building,
  Wifi,
  Heart,
  CheckCircle,
  ArrowRight,
  Send,
  Save,
  Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { useTranslation } from '../contexts/TranslationContext';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function SettingCard({ title, description, children }) {
  return (
    <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6">
      <h3 className="font-barlow font-bold text-lg mb-1">{title}</h3>
      {description && <p className="text-sm text-[#52525b] mb-4">{description}</p>}
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
}

function SettingRow({ label, description, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 py-3 border-b border-[#27272a] last:border-0">
      <div className="flex-1">
        <Label className="text-sm font-medium">{label}</Label>
        {description && <p className="text-xs text-[#52525b] mt-0.5">{description}</p>}
      </div>
      <div>{children}</div>
    </div>
  );
}

function HowItWorksStep({ number, icon: Icon, title, description }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0">
        <div className="w-10 h-10 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
          <span className="font-barlow font-bold text-blue-500">{number}</span>
        </div>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="h-4 w-4 text-blue-500" />
          <h4 className="font-medium">{title}</h4>
        </div>
        <p className="text-sm text-[#a1a1aa]">{description}</p>
      </div>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    green: 'bg-green-500/10 text-green-500 border-green-500/20',
    orange: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
    red: 'bg-red-500/10 text-red-500 border-red-500/20',
    purple: 'bg-purple-500/10 text-purple-500 border-purple-500/20'
  };
  
  return (
    <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-5">
      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center mb-3 border", colors[color])}>
        <Icon className="h-5 w-5" />
      </div>
      <h4 className="font-medium mb-1">{title}</h4>
      <p className="text-sm text-[#a1a1aa]">{description}</p>
    </div>
  );
}

export default function GeneralSettings() {
  const { t, language, setLanguage } = useTranslation();
  const { isDark, toggleTheme, setTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modified, setModified] = useState(false);
  const [settings, setSettings] = useState({
    darkMode: true,
    language: 'EN',
    autoRefresh: true,
    refreshInterval: 30,
    soundAlerts: true,
    desktopNotifications: false,
    defaultEscalationMinutes: 5,
    residenceName: 'Eldera Demo Residence',
    residenceAddress: 'Riga, Latvia',
    supportEmail: 'info@0gbaltics.com',
    supportPhone: '+371 28 650 996'
  });
  
  const [sendingTest, setSendingTest] = useState(false);

  // Load settings from backend
  useEffect(() => {
    async function fetchSettings() {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/general-settings`);
        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({ ...prev, ...data }));
          // Sync dark mode with theme context
          if (data.dark_mode !== undefined) {
            setTheme(data.dark_mode ? 'dark' : 'light');
          }
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchSettings();
  }, [setTheme]);

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setModified(true);
    
    // Special handling for language change
    if (key === 'language') {
      setLanguage(value);
    }
    
    // Special handling for dark mode - apply immediately
    if (key === 'darkMode') {
      setTheme(value ? 'dark' : 'light');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${BACKEND_URL}/api/general-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          residence_name: settings.residenceName,
          residence_address: settings.residenceAddress,
          dark_mode: settings.darkMode,
          language: settings.language,
          auto_refresh: settings.autoRefresh,
          refresh_interval: settings.refreshInterval,
          sound_alerts: settings.soundAlerts,
          desktop_notifications: settings.desktopNotifications,
          default_escalation_minutes: settings.defaultEscalationMinutes,
          support_email: settings.supportEmail,
          support_phone: settings.supportPhone
        })
      });
      
      if (response.ok) {
        toast.success(t('save_changes') + ' ✓');
        setModified(false);
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestNotifications = async () => {
    setSendingTest(true);
    try {
      // Test email
      const emailRes = await fetch(`${BACKEND_URL}/api/test-email`, { method: 'POST' });
      if (emailRes.ok) {
        toast.success('Test email sent!');
      }
      
      // Test SMS if phone provided
      if (settings.supportPhone) {
        const smsRes = await fetch(`${BACKEND_URL}/api/test-sms?to_phone=${encodeURIComponent(settings.supportPhone)}`, { method: 'POST' });
        if (smsRes.ok) {
          toast.success('Test SMS sent!');
        }
      }
    } catch (err) {
      toast.error('Failed to send test notifications');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 pt-16 sm:pt-4" data-testid="general-settings-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-barlow font-bold text-xl sm:text-2xl md:text-3xl tracking-tight flex items-center gap-3">
            <Settings className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
            {t('general_settings')}
          </h1>
          <p className="text-[#a1a1aa] text-sm mt-1">
            {t('welcome_desc')}
          </p>
        </div>
        
        {modified && (
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {t('save_changes')}
          </Button>
        )}
      </div>
      
      {modified && (
        <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-3 mb-6 flex items-center gap-2 text-orange-500 text-sm">
          <AlertTriangle className="h-4 w-4" />
          You have unsaved changes
        </div>
      )}
      
      {/* Tabs */}
      <Tabs defaultValue="settings" className="space-y-6">
        <TabsList className="bg-[#18181b] border border-[#27272a]">
          <TabsTrigger value="settings" data-testid="tab-settings">
            <Settings className="h-4 w-4 mr-2" />
            {t('settings')}
          </TabsTrigger>
          <TabsTrigger value="how-it-works" data-testid="tab-how-it-works">
            <Info className="h-4 w-4 mr-2" />
            {t('how_it_works')}
          </TabsTrigger>
        </TabsList>
        
        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          {/* Residence Information */}
          <SettingCard title={t('residence_information')} description="">
            <SettingRow label={t('residence_information')} description="">
              <Input
                value={settings.residenceName}
                onChange={(e) => handleChange('residenceName', e.target.value)}
                className="bg-[#09090b] border-[#27272a] w-64"
                data-testid="residence-name"
              />
            </SettingRow>
            <SettingRow label={t('location')} description="">
              <Input
                value={settings.residenceAddress}
                onChange={(e) => handleChange('residenceAddress', e.target.value)}
                className="bg-[#09090b] border-[#27272a] w-64"
                data-testid="residence-address"
              />
            </SettingRow>
          </SettingCard>
          
          {/* Display Settings */}
          <SettingCard title={t('display_settings')} description="">
            <SettingRow label={t('dark_mode')} description="">
              <Switch
                checked={settings.darkMode}
                onCheckedChange={(checked) => handleChange('darkMode', checked)}
                data-testid="dark-mode-toggle"
              />
            </SettingRow>
            <SettingRow label={t('language')} description="">
              <select
                value={settings.language}
                onChange={(e) => handleChange('language', e.target.value)}
                className="bg-[#09090b] border border-[#27272a] rounded-md px-3 py-2 text-sm w-32"
                data-testid="language-select"
              >
                <option value="EN">English</option>
                <option value="FR">Français</option>
                <option value="DE">Deutsch</option>
                <option value="LV">Latviešu</option>
                <option value="LT">Lietuvių</option>
              </select>
            </SettingRow>
          </SettingCard>
          
          {/* Dashboard Behavior */}
          <SettingCard title={t('dashboard_behavior')} description="">
            <SettingRow label={t('auto_refresh')} description="">
              <Switch
                checked={settings.autoRefresh}
                onCheckedChange={(checked) => handleChange('autoRefresh', checked)}
                data-testid="auto-refresh-toggle"
              />
            </SettingRow>
            <SettingRow label={t('refresh_interval')} description="">
              <Input
                type="number"
                value={settings.refreshInterval}
                onChange={(e) => handleChange('refreshInterval', parseInt(e.target.value))}
                className="bg-[#09090b] border-[#27272a] w-24"
                min={10}
                max={300}
                data-testid="refresh-interval"
              />
            </SettingRow>
            <SettingRow label={t('sound_alerts')} description="">
              <Switch
                checked={settings.soundAlerts}
                onCheckedChange={(checked) => handleChange('soundAlerts', checked)}
                data-testid="sound-alerts-toggle"
              />
            </SettingRow>
            <SettingRow label={t('desktop_notifications')} description="">
              <Switch
                checked={settings.desktopNotifications}
                onCheckedChange={(checked) => handleChange('desktopNotifications', checked)}
                data-testid="desktop-notifications-toggle"
              />
            </SettingRow>
          </SettingCard>
          
          {/* Alert Defaults */}
          <SettingCard title={t('alert_defaults')} description="">
            <SettingRow label={t('default_escalation')} description="">
              <Input
                type="number"
                value={settings.defaultEscalationMinutes}
                onChange={(e) => handleChange('defaultEscalationMinutes', parseInt(e.target.value))}
                className="bg-[#09090b] border-[#27272a] w-24"
                min={1}
                max={60}
                data-testid="default-escalation"
              />
            </SettingRow>
          </SettingCard>
          
          {/* Support Contact */}
          <SettingCard title={t('support_contact')} description="">
            <SettingRow label="Email" description="">
              <Input
                type="email"
                value={settings.supportEmail}
                onChange={(e) => handleChange('supportEmail', e.target.value)}
                className="bg-[#09090b] border-[#27272a] w-64"
                data-testid="support-email"
              />
            </SettingRow>
            <SettingRow label="Phone" description="">
              <Input
                type="tel"
                value={settings.supportPhone}
                onChange={(e) => handleChange('supportPhone', e.target.value)}
                className="bg-[#09090b] border-[#27272a] w-48"
                data-testid="support-phone"
              />
            </SettingRow>
            <div className="pt-4">
              <Button
                variant="outline"
                onClick={handleTestNotifications}
                disabled={sendingTest}
                data-testid="test-notifications-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                {sendingTest ? '...' : t('test_email_sms')}
              </Button>
            </div>
          </SettingCard>
        </TabsContent>
        
        {/* How It Works Tab */}
        <TabsContent value="how-it-works" className="space-y-8">
          {/* Introduction */}
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-8">
            <h2 className="font-barlow font-bold text-2xl mb-3">{t('welcome')}</h2>
            <p className="text-[#a1a1aa] max-w-2xl">
              {t('welcome_desc')}
            </p>
          </div>
          
          {/* How It Works Steps */}
          <SettingCard title="How the Platform Works" description="Step-by-step overview of the monitoring system">
            <div className="space-y-6 py-2">
              <HowItWorksStep
                number="1"
                icon={Wifi}
                title="Sensors Collect Data"
                description="Smart sensors in each apartment monitor activity, air quality (temperature, humidity, CO2), and detect events like fridge openings for breakfast tracking."
              />
              <div className="flex justify-center">
                <ArrowRight className="h-5 w-5 text-[#52525b]" />
              </div>
              <HowItWorksStep
                number="2"
                icon={Activity}
                title="Wearable Devices Track Health"
                description="Each resident wears a bracelet that monitors their location, detects falls, and allows them to trigger medical alerts with a button press."
              />
              <div className="flex justify-center">
                <ArrowRight className="h-5 w-5 text-[#52525b]" />
              </div>
              <HowItWorksStep
                number="3"
                icon={Bell}
                title="Alerts Are Generated"
                description="When anomalies are detected (falls, missed breakfast, high CO2, device offline), the system automatically creates alerts with severity levels."
              />
              <div className="flex justify-center">
                <ArrowRight className="h-5 w-5 text-[#52525b]" />
              </div>
              <HowItWorksStep
                number="4"
                icon={Users}
                title="Concierge Takes Action"
                description="The concierge monitors the dashboard, acknowledges alerts, and resolves them after verifying the resident's status."
              />
              <div className="flex justify-center">
                <ArrowRight className="h-5 w-5 text-[#52525b]" />
              </div>
              <HowItWorksStep
                number="5"
                icon={Mail}
                title="Escalation if Needed"
                description="If alerts aren't acknowledged within the configured time, automatic notifications (email/SMS) are sent to family, doctors, or emergency services."
              />
            </div>
          </SettingCard>
          
          {/* Features Grid */}
          <div>
            <h3 className="font-barlow font-bold text-lg mb-4">Key Features</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <FeatureCard
                icon={AlertTriangle}
                title="Fall Detection"
                description="Automatic detection of falls with immediate alert creation and configurable sensitivity."
                color="red"
              />
              <FeatureCard
                icon={Heart}
                title="Medical Alerts"
                description="One-button emergency alerts from the bracelet for medical assistance."
                color="red"
              />
              <FeatureCard
                icon={Thermometer}
                title="Air Quality Monitoring"
                description="Real-time tracking of temperature, humidity, and CO2 levels in each apartment."
                color="blue"
              />
              <FeatureCard
                icon={Activity}
                title="Activity Scoring"
                description="Daily activity analysis based on movement patterns and sensor interactions."
                color="green"
              />
              <FeatureCard
                icon={Clock}
                title="Breakfast Tracking"
                description="Monitors fridge activity to ensure residents have their morning meal."
                color="orange"
              />
              <FeatureCard
                icon={Globe}
                title="Location Tracking"
                description="Know when residents are outside the residence with GPS tracker integration."
                color="purple"
              />
            </div>
          </div>
          
          {/* Alert Types */}
          <SettingCard title="Alert Types & Severity" description="Understanding the different alert levels">
            <div className="space-y-3">
              <div className="flex items-center gap-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <div className="px-3 py-1 rounded-full bg-red-500/20 text-red-500 text-xs font-bold">CRITICAL</div>
                <div>
                  <div className="font-medium text-sm">Fall Detection</div>
                  <div className="text-xs text-[#a1a1aa]">Immediate attention required. Auto-escalates in 3 minutes.</div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20">
                <div className="px-3 py-1 rounded-full bg-orange-500/20 text-orange-500 text-xs font-bold">HIGH</div>
                <div>
                  <div className="font-medium text-sm">Medical Alert / Device Offline</div>
                  <div className="text-xs text-[#a1a1aa]">Urgent attention needed. Auto-escalates in 5 minutes.</div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                <div className="px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-500 text-xs font-bold">MEDIUM</div>
                <div>
                  <div className="font-medium text-sm">Air Quality Warning / Missed Breakfast</div>
                  <div className="text-xs text-[#a1a1aa]">Should be checked soon. Auto-escalates in 15 minutes.</div>
                </div>
              </div>
              <div className="flex items-center gap-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <div className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-500 text-xs font-bold">LOW</div>
                <div>
                  <div className="font-medium text-sm">Low Activity / Battery Warning</div>
                  <div className="text-xs text-[#a1a1aa]">Informational. No auto-escalation.</div>
                </div>
              </div>
            </div>
          </SettingCard>
          
          {/* User Roles */}
          <SettingCard title="User Roles" description="Different views for different users">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-[#09090b]/50 border border-[#27272a]">
                <div className="flex items-center gap-2 mb-2">
                  <Building className="h-5 w-5 text-orange-500" />
                  <span className="font-medium">Concierge</span>
                  <span className="text-[10px] bg-green-500/20 text-green-500 px-2 py-0.5 rounded-full">ACTIVE</span>
                </div>
                <p className="text-sm text-[#a1a1aa]">Full dashboard access, alert management, resident settings. Primary monitoring role.</p>
              </div>
              <div className="p-4 rounded-lg bg-[#09090b]/50 border border-[#27272a] opacity-60">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-purple-500" />
                  <span className="font-medium">Family</span>
                  <span className="text-[10px] bg-[#52525b]/50 text-[#a1a1aa] px-2 py-0.5 rounded-full">COMING SOON</span>
                </div>
                <p className="text-sm text-[#a1a1aa]">View-only access to their family member's status and activity summary.</p>
              </div>
              <div className="p-4 rounded-lg bg-[#09090b]/50 border border-[#27272a] opacity-60">
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="h-5 w-5 text-green-500" />
                  <span className="font-medium">Doctor</span>
                  <span className="text-[10px] bg-[#52525b]/50 text-[#a1a1aa] px-2 py-0.5 rounded-full">COMING SOON</span>
                </div>
                <p className="text-sm text-[#a1a1aa]">Full medical history, detailed sensor data, and health trends analysis.</p>
              </div>
              <div className="p-4 rounded-lg bg-[#09090b]/50 border border-[#27272a] opacity-60">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="h-5 w-5 text-blue-500" />
                  <span className="font-medium">Medical Staff</span>
                  <span className="text-[10px] bg-[#52525b]/50 text-[#a1a1aa] px-2 py-0.5 rounded-full">COMING SOON</span>
                </div>
                <p className="text-sm text-[#a1a1aa]">Vitals monitoring, medication schedules, and visit history tracking.</p>
              </div>
            </div>
          </SettingCard>
          
          {/* Contact */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-xl p-6 text-center">
            <h3 className="font-barlow font-bold text-lg mb-2">{t('need_help')}</h3>
            <p className="text-[#a1a1aa] mb-4">{t('contact_support')}</p>
            <div className="flex items-center justify-center gap-4">
              <a href="mailto:info@0gbaltics.com" className="flex items-center gap-2 text-blue-500 hover:underline">
                <Mail className="h-4 w-4" />
                info@0gbaltics.com
              </a>
              <span className="text-[#52525b]">|</span>
              <a href="tel:+37128650996" className="flex items-center gap-2 text-blue-500 hover:underline">
                <Phone className="h-4 w-4" />
                +371 28 650 996
              </a>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
